import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BusApi, OfficeApi, SpawnsApi, SpritesApi } from "../lib/api";
import { qk } from "../lib/query";
import type { SpawnDetail, SpawnMeta } from "../lib/types";
import {
  STAGE, DESKS, DOOR, WALL_H, INTERIOR_W, advanceAlong, OfficeSim,
  SERVER_ROOM, SERVER_DESKS, MEETING_ROOM, MEETING_TABLE, PANTRY, PANTRY_TABLE,
  GARDEN_X, GATE, POND, TREES, BENCHES,
} from "../lib/officeSim";
import type { CatState, GuestState, ObservedSpec, SimEntity, VisitorState } from "../lib/officeSim";
import { PageBody, PageHeader } from "../components/PageScaffold";
import { Maximize, X, ZoomIn, ZoomOut } from "lucide-react";
// Bundle the sheet through Vite → served from /assets/ (hashed, cache-busted).
// NOTE: do NOT put it under /visualize/* — that path is this page's SPA route and
// would shadow the file (the server returns index.html for it → broken image).
import charsSheet from "../assets/chars.png";

// ── Sprite sheet geometry (Kenney roguelike-characters, CC0) ────────────────
// Fallback for personas without a generated character sprite.
const SHEET = charsSheet;
const TILE = 16, STRIDE = 17, ZOOM = 3;
const CHAR_TILES: Array<[number, number]> = [];
for (const row of [3, 5, 6, 7, 8, 9]) for (let col = 0; col < 2; col++) CHAR_TILES.push([col, row]);

function hash(s: string): number {
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h;
}
function spriteFor(name: string): { backgroundPosition: string } {
  const [col, row] = CHAR_TILES[hash(name) % CHAR_TILES.length];
  return { backgroundPosition: `-${col * STRIDE}px -${row * STRIDE}px` };
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Strip tool/command/JSON noise → only the persona's spoken prose. claude
 *  personas' stdout is already clean; codex ones carry command echoes. */
function spokenLines(stdout: string): string[] {
  return stdout.split("\n").map((l) => l.trimEnd()).filter((l) => {
    const t = l.trim();
    if (!t) return false;
    if (/^[$#>]/.test(t)) return false;
    if (/^(git|gh|bash|cd|ls|cat|grep|npm|bun|node|python3?|sqlite3|rm|mkdir|echo|curl|sed|awk|tail|head|find|cp|mv|chmod|export|env)\b/.test(t)) return false;
    if (/^[[{].*[\]}],?$/.test(t)) return false;                 // json-ish line
    if (/^(●|⎿|✳|✱|·|—{3,}|-{3,}|={3,}|\+{2,}|\|)/.test(t)) return false; // tool/diff markers
    if (/^\/(bin|home|data|usr|tmp)\//.test(t)) return false;    // bare paths
    return true;
  });
}

/** What a working persona's speech bubble says: its latest spoken line, else
 *  what it was spawned FOR (trigger), else how long it's been thinking.
 *
 *  `visitorPresent` — a visitor sprite for THIS spawn is on screen saying the
 *  inbound message itself. When that's true the persona must not repeat it:
 *  both bubbles read `source.messages[last].text` off the same envelope and
 *  both cut at 90 chars, so they rendered the identical string side by side
 *  until the persona produced its first line of output. Show who it's from and
 *  that it's still reading instead — the words belong to the sender's bubble. */
function bubbleText(d: SpawnDetail, visitorPresent = false): string {
  const spoken = (spokenLines(d.stdout).at(-1) ?? "").slice(0, 160);
  if (spoken) return spoken;
  const t = d.trigger;
  if (t) {
    // Who it's from: the SENDER first. chat_name is the room, not the speaker,
    // and our chats are named after the persona — so leading with it produced
    // "📨 plutus: hi plutus, …" floating above plutus, reading as if it were
    // talking to itself.
    const who = t.sender || t.chat_name || t.platform || t.kind || "";
    if (visitorPresent) {
      const secs = Math.max(0, Math.floor((Date.now() - Date.parse(d.meta.started_at)) / 1000));
      return who ? `📨 from ${who} — reading… ${secs}s` : `⚙ reading the task… ${secs}s`;
    }
    // No visitor on screen (internal/bus wakes have none), so nothing to
    // duplicate — a snippet is the most useful thing to show.
    const lastMsg = t.messages.length ? t.messages[t.messages.length - 1].text : "";
    const bodyClean = (t.body ?? "").split("\n").filter((l) => !l.trim().startsWith(">")).join(" ");
    const what = (lastMsg || bodyClean || t.subject || "").replace(/\s+/g, " ").trim().slice(0, 90);
    if (who || what) return `📨 ${who}${who && what ? ": " : ""}${what}`;
  }
  const secs = Math.max(0, Math.floor((Date.now() - Date.parse(d.meta.started_at)) / 1000));
  return `⚙ reading the task… ${secs}s`;
}

/** Real WITA time drives the scene: sky in the windows, garden light, lamps. */
function calcPhase(): "day" | "dusk" | "night" | "dawn" {
  const h = Number(new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Makassar", hour: "numeric", hour12: false }).format(new Date())) % 24;
  if (h >= 6 && h < 17) return "day";
  if (h >= 17 && h < 19) return "dusk";
  if (h >= 5 && h < 6) return "dawn";
  return "night";
}
function useDayPhase() {
  const [phase, setPhase] = useState(calcPhase);
  useEffect(() => {
    const t = setInterval(() => setPhase(calcPhase()), 60_000);
    return () => clearInterval(t);
  }, []);
  return phase;
}

export default function Visualize() {
  // ── Fleet state feeds ────────────────────────────────────────────────────
  const { data: spawns = [] } = useQuery({
    queryKey: qk.spawns.list({ viz: 1 }),
    queryFn: () => SpawnsApi.list({ limit: 500 }),
    refetchInterval: 2000,
  });
  const { data: spriteJobs = [] } = useQuery({
    queryKey: ["sprites"],
    queryFn: SpritesApi.list,
    refetchInterval: 10_000,
  });
  // Recent bus chatter — the activity ticker.
  const { data: busMsgs = [] } = useQuery({
    queryKey: ["viz-bus"],
    queryFn: () => BusApi.messages({ limit: 15 }),
    refetchInterval: 5000,
  });
  // The office simulation runs server-side (one authoritative instance —
  // every open browser sees the same office). We poll snapshots and
  // interpolate movement between them.
  const { data: snap, dataUpdatedAt } = useQuery({
    queryKey: ["office"],
    queryFn: OfficeApi.snapshot,
    refetchInterval: 2000,
  });

  const phase = useDayPhase();

  const spriteUrls = useMemo(() => {
    const m = new Map<string, string>();
    for (const j of spriteJobs) if (j.has_png) m.set(j.persona, SpritesApi.pngUrl(j.persona, j.updated_at));
    return m;
  }, [spriteJobs]);

  const runningByPersona = useMemo(() => {
    const m = new Map<string, SpawnMeta>();
    for (const s of spawns) {
      if (s.status !== "running") continue;
      const cur = m.get(s.persona);
      if (!cur || s.started_at > cur.started_at) m.set(s.persona, s);
    }
    return m;
  }, [spawns]);

  // Spawn ids whose SENDER is on screen with a visible bubble (visitor.id is the
  // spawn id — see office.ts). Those personas must not echo the same message
  // back; the condition mirrors the visitor's own render test below.
  const speakingVisitors = useMemo(() => {
    const s = new Set<string>();
    for (const v of snap?.visitors ?? []) if (v.phase === "standing" && v.snippet) s.add(v.id);
    return s;
  }, [snap?.visitors]);

  // ── Client-side interpolation over server snapshots ─────────────────────
  // Authoritative state lands every ~2s; each frame we extrapolate along the
  // entity's remaining path (same movement math as the server) and ease the
  // displayed position toward it. No React render per frame.
  const authRef = useRef<{ at: number; entities: SimEntity[]; cat: CatState | null; visitors: VisitorState[]; guests: GuestState[] }>({ at: 0, entities: [], cat: null, visitors: [], guests: [] });
  useEffect(() => {
    if (snap) authRef.current = { at: dataUpdatedAt, entities: snap.entities, cat: snap.cat ?? null, visitors: snap.visitors ?? [], guests: snap.guests ?? [] };
  }, [snap, dataUpdatedAt]);

  const actorEls = useRef(new Map<string, HTMLDivElement | null>());
  const flipEls = useRef(new Map<string, HTMLDivElement | null>());
  const walkEls = useRef(new Map<string, HTMLDivElement | null>());
  const catEl = useRef<HTMLDivElement | null>(null);
  const catFlipEl = useRef<HTMLDivElement | null>(null);
  const displayPos = useRef(new Map<string, { x: number; y: number; face: 1 | -1 }>());
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const { at, entities: auth, cat, visitors: authVisitors } = authRef.current;
      const dt = Math.max(0, (Date.now() - at) / 1000);
      const movers: Array<{ key: string; e: { x: number; y: number; face: 1 | -1; path: Array<{ x: number; y: number }>; speed: number }; el: HTMLDivElement | null; flip: HTMLDivElement | null; walk: HTMLDivElement | null; w: number; h: number }> = [];
      for (const e of auth) {
        if (!e.visible) { displayPos.current.delete(e.name); continue; }
        movers.push({ key: e.name, e, el: actorEls.current.get(e.name) ?? null, flip: flipEls.current.get(e.name) ?? null, walk: walkEls.current.get(e.name) ?? null, w: TILE * ZOOM, h: TILE * ZOOM });
      }
      for (const v of authVisitors) {
        const key = `v:${v.id}`;
        movers.push({ key, e: v, el: actorEls.current.get(key) ?? null, flip: flipEls.current.get(key) ?? null, walk: walkEls.current.get(key) ?? null, w: TILE * ZOOM, h: TILE * ZOOM });
      }
      for (const g of authRef.current.guests) {
        const key = `g:${g.key}`;
        movers.push({ key, e: g, el: actorEls.current.get(key) ?? null, flip: flipEls.current.get(key) ?? null, walk: walkEls.current.get(key) ?? null, w: TILE * ZOOM, h: TILE * ZOOM });
      }
      if (cat) movers.push({ key: "__cat", e: cat, el: catEl.current, flip: catFlipEl.current, walk: null, w: 32, h: 32 });
      for (const m of movers) {
        const e = m.e;
        const ext = e.path.length > 0 ? advanceAlong(e.x, e.y, e.path, e.speed * dt) : { x: e.x, y: e.y };
        let d = displayPos.current.get(m.key);
        if (!d) { d = { x: ext.x, y: ext.y, face: e.face }; displayPos.current.set(m.key, d); }
        const ddx = ext.x - d.x, ddy = ext.y - d.y;
        if (Math.hypot(ddx, ddy) > 80) { d.x = ext.x; d.y = ext.y; }   // teleport (server restart etc.)
        else { d.x += ddx * 0.18; d.y += ddy * 0.18; }
        const walking = Math.hypot(ddx, ddy) > 1.2;
        d.face = Math.abs(ddx) > 0.8 ? (ddx > 0 ? 1 : -1) : e.face;
        if (!m.el) continue;
        m.el.style.transform = `translate3d(${d.x - m.w / 2}px, ${d.y - m.h}px, 0)`;
        m.el.style.zIndex = String(100 + Math.round(d.y));
        if (m.flip) m.flip.style.transform = `scaleX(${d.face})`;
        if (m.walk) m.walk.classList.toggle("viz-walk", walking);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const entities = snap?.entities ?? [];
  const visible = entities.filter((e) => e.visible);
  const fails = snap?.fails ?? {};
  const unread = snap?.unread ?? {};
  const dones = snap?.dones ?? {};
  const star = snap?.star ?? null;
  const raining = snap?.weather === "rain";
  const counts = {
    working: entities.filter((e) => e.mode === "working").length,
    chatting: entities.filter((e) => e.mode === "meeting" || e.mode === "visiting").length,
    observing: entities.filter((e) => e.mode === "observing").length,
    wandering: entities.filter((e) => e.mode === "wandering").length,
    resting: entities.filter((e) => e.mode === "resting").length,
    away: entities.filter((e) => e.mode === "away" || e.mode === "leaving").length,
  };
  // "+" only when the capped list is entirely younger than an hour (count clipped).
  const spawnsLastHour = useMemo(
    () => spawns.filter((s) => Date.parse(s.started_at) > Date.now() - 3_600_000).length,
    [spawns],
  );
  const hourClipped = spawns.length >= 500 && spawnsLastHour === spawns.length;
  const failCount = Object.keys(fails).length;

  // ── Activity ticker ──────────────────────────────────────────────────────
  const events = useMemo(() => {
    const ev: Array<{ at: number; text: string }> = [];
    for (const s of spawns.slice(0, 25)) {
      if (s.status === "failed" && s.ended_at) ev.push({ at: Date.parse(s.ended_at), text: `💥 ${s.persona} spawn failed` });
      ev.push({ at: Date.parse(s.started_at), text: `⚡ ${s.persona} spawned` });
    }
    for (const m of busMsgs) {
      if (m.from_agent === m.to_agent) continue;
      ev.push({ at: Date.parse(m.sent_at), text: `✉️ ${m.from_agent} → ${m.to_agent}${m.subject ? ` — ${m.subject}` : ""}` });
    }
    return ev.filter((e) => Number.isFinite(e.at)).sort((a, b) => b.at - a.at).slice(0, 12);
  }, [spawns, busMsgs]);
  const [tickerIdx, setTickerIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTickerIdx((i) => i + 1), 4000);
    return () => clearInterval(t);
  }, []);
  const tickerEvent = events.length ? events[tickerIdx % events.length] : null;

  const [selected, setSelected] = useState<string | null>(null);
  const [selectedChat, setSelectedChat] = useState<ObservedSpec | null>(null);
  const [showApprovals, setShowApprovals] = useState(false);
  const approvals = snap?.approvals ?? [];
  const waitingOnHuman = approvals.filter((a) => a.state === "awaiting_human");

  // ── Pan / zoom viewport (mobile pinch + drag, desktop wheel + drag) ──────
  const viewportRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const [viewH, setViewH] = useState(STAGE.h);
  const touched = useRef(false);           // user has zoomed/panned → stop auto-fit
  const moved = useRef(false);             // drag happened → swallow the click
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; scale: number; tx: number; ty: number; mx: number; my: number } | null>(null);

  const fitView = () => {
    const el = viewportRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const s = Math.min(1, w / STAGE.w);
    const h = s < 1 ? Math.max(340, Math.round(STAGE.h * s)) : STAGE.h;
    setViewH(h);
    setView({ scale: s, tx: Math.max(0, (w - STAGE.w * s) / 2), ty: Math.max(0, (h - STAGE.h * s) / 2) });
  };

  useEffect(() => {
    fitView();
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { if (!touched.current) fitView(); });
    ro.observe(el);
    // Wheel must be a non-passive native listener to preventDefault page scroll.
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      touched.current = true;
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      setView((v) => {
        const s = clamp(v.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), 0.3, 4);
        const wx = (mx - v.tx) / v.scale, wy = (my - v.ty) / v.scale;
        return { scale: s, tx: mx - wx * s, ty: my - wy * s };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => { ro.disconnect(); el.removeEventListener("wheel", onWheel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zoomBy = (f: number) => {
    const el = viewportRef.current;
    if (!el) return;
    touched.current = true;
    const mx = el.clientWidth / 2, my = el.clientHeight / 2;
    setView((v) => {
      const s = clamp(v.scale * f, 0.3, 4);
      const wx = (mx - v.tx) / v.scale, wy = (my - v.ty) / v.scale;
      return { scale: s, tx: mx - wx * s, ty: my - wy * s };
    });
  };
  const resetView = () => { touched.current = false; fitView(); };

  const onPointerDown = (e: React.PointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: view.scale, tx: view.tx, ty: view.ty, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
    }
    if (pointers.current.size === 1) moved.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    if (pointers.current.size === 1) {
      const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
      if (moved.current || Math.abs(dx) + Math.abs(dy) > 3) {
        moved.current = true;
        touched.current = true;
        setView((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
      }
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    } else if (pointers.current.size === 2 && pinch.current) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const [a, b] = [...pointers.current.values()];
      const ps = pinch.current;
      const el = viewportRef.current!;
      const rect = el.getBoundingClientRect();
      touched.current = true;
      moved.current = true;
      const s = clamp((ps.scale * Math.hypot(a.x - b.x, a.y - b.y)) / ps.dist, 0.3, 4);
      const mx = (a.x + b.x) / 2 - rect.left, my = (a.y + b.y) / 2 - rect.top;
      const wx = (ps.mx - rect.left - ps.tx) / ps.scale, wy = (ps.my - rect.top - ps.ty) / ps.scale;
      setView({ scale: s, tx: mx - wx * s, ty: my - wy * s });
    }
  };
  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    pinch.current = null;
  };

  const now = Date.now();

  return (
    <>
      <PageHeader
        title="Visualize"
        subtitle={`the fleet as an office — ${counts.working} working, ${counts.chatting + counts.wandering + counts.resting} around, ${counts.away} away`}
      />
      <PageBody>
        <style>{keyframes}</style>
        <div
          ref={viewportRef}
          className="viz-viewport relative overflow-hidden rounded-lg shadow-inner"
          style={{ height: viewH, touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onClickCapture={(e) => { if (moved.current) { e.stopPropagation(); moved.current = false; } }}
        >
          {/* HUD + zoom controls */}
          <div className="viz-hud absolute left-2 top-2 z-30" onPointerDown={(e) => e.stopPropagation()}>
            🖥 {counts.working} · 💬 {counts.chatting} · 👁 {counts.observing} · 🚶 {counts.wandering} · 🛋 {counts.resting} · 🚪 {counts.away}
            {failCount > 0 && <span className="viz-hud-alert"> · 💥 {failCount}</span>}
            {waitingOnHuman.length > 0 && <span className="viz-hud-alert"> · 📄 {waitingOnHuman.length}</span>}
            {" "}· ⚡ {spawnsLastHour}{hourClipped ? "+" : ""}/h{raining ? " · 🌧" : ""}
          </div>
          <div className="absolute right-2 top-2 z-30 flex flex-col gap-1" onPointerDown={(e) => e.stopPropagation()}>
            <button type="button" className="viz-zoombtn" onClick={() => zoomBy(1.3)} aria-label="Zoom in"><ZoomIn className="size-4" /></button>
            <button type="button" className="viz-zoombtn" onClick={() => zoomBy(1 / 1.3)} aria-label="Zoom out"><ZoomOut className="size-4" /></button>
            <button type="button" className="viz-zoombtn" onClick={resetView} aria-label="Fit to screen"><Maximize className="size-4" /></button>
          </div>
          {/* activity ticker */}
          {tickerEvent && (
            <div className="viz-ticker absolute bottom-2 left-2 z-30" key={tickerIdx}>
              <span className="opacity-60">{agoText(now - tickerEvent.at)} ago</span> {tickerEvent.text}
            </div>
          )}
          <div
            className={`viz-stage viz-phase-${phase} absolute rounded-lg`}
            style={{ width: STAGE.w, height: STAGE.h, transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`, transformOrigin: "0 0" }}
          >
            {/* floor + back wall (interior only) */}
            <div className="viz-floor absolute" style={{ left: 0, top: 0, width: INTERIOR_W, height: STAGE.h }} />
            <div className="viz-wall absolute top-0" style={{ left: 0, width: INTERIOR_W, height: WALL_H }} />
            {/* garden (outdoors, right strip) */}
            <div className="viz-garden absolute" style={{ left: GARDEN_X, top: 0, width: STAGE.w - GARDEN_X, height: STAGE.h }} />
            <div className="viz-fence absolute" style={{ left: GARDEN_X - 3, top: WALL_H, width: 6, height: GATE.y - WALL_H }} />
            <div className="viz-fence absolute" style={{ left: GARDEN_X - 3, top: GATE.y + GATE.h, width: 6, height: STAGE.h - GATE.y - GATE.h }} />
            <div className="viz-pond absolute" style={{ left: POND.x, top: POND.y, width: POND.w, height: POND.h }} />
            {TREES.map((t, i) => <div key={i} className="viz-tree absolute" style={{ left: t.x - 16, top: t.y - 26 }} />)}
            {BENCHES.map((b, i) => <div key={i} className="viz-bench absolute" style={{ left: b.x, top: b.y }} />)}
            <div className="viz-flowers absolute" style={{ left: 925, top: 205 }} />
            <div className="viz-flowers absolute" style={{ left: 1120, top: 235 }} />
            <div className="viz-flowers absolute" style={{ left: 1000, top: 130 }} />
            <div className="viz-label absolute" style={{ left: GARDEN_X + 12, top: 66 }}>garden</div>
            {raining && <div className="viz-rain absolute" style={{ left: GARDEN_X, top: 0, width: STAGE.w - GARDEN_X, height: STAGE.h }} />}
            {/* wall dressing: whiteboard, door, windows, clock */}
            <div className="viz-board absolute" style={{ left: 60, top: 8 }} />
            {/* operator desk — pending approvals pile up here as papers */}
            <button
              type="button"
              className="viz-opdesk absolute"
              style={{ left: 160, top: 62 }}
              onClick={() => setShowApprovals(true)}
              title={approvals.length ? `${approvals.length} assist session(s) — ${waitingOnHuman.length} waiting on you` : "operator desk — no pending approvals"}
            >
              <span className="viz-opdesk-label">harry</span>
              {approvals.slice(0, 6).map((a, i) => (
                <span
                  key={a.sid}
                  className={a.state === "awaiting_human" ? "viz-paper viz-paper-hot" : "viz-paper"}
                  style={{ left: 4 + i * 7, top: -6 - (i % 3) * 2, transform: `rotate(${(i % 3) - 1}0deg)` }}
                />
              ))}
            </button>
            {star && (
              <div className="viz-plaque absolute" style={{ left: 262, top: 10 }} title={`employee of the day — ${star.count} spawns completed`}>
                <span className="viz-plaque-title">star of the day</span>
                <span className="viz-plaque-name">⭐ {star.name}</span>
              </div>
            )}
            <div className="viz-door absolute" style={{ left: DOOR.x - 15, top: 10 }} />
            <div className="viz-window absolute" style={{ left: 320, top: 8 }} />
            <div className="viz-window absolute" style={{ left: 470, top: 8 }} />
            <div className="viz-window absolute" style={{ left: 620, top: 8 }} />
            <div className="viz-clock absolute" style={{ left: 700, top: 14 }} />
            {/* lounge rug + couch, plants */}
            <div className="viz-rug absolute" style={{ left: 24, top: 96, width: 210, height: 260 }} />
            <div className="viz-couch absolute" style={{ left: 40, top: 120, width: 84, height: 30 }} />
            <div className="viz-label absolute" style={{ left: 30, top: 100 }}>lounge</div>
            <div className="viz-plant absolute" style={{ left: 10, top: 62 }} />
            <div className="viz-plant absolute" style={{ left: 252, top: 62 }} />
            <div className="viz-plant absolute" style={{ left: 590, top: 420 }} />
            {/* pantry */}
            <div className="viz-pantry absolute" style={{ left: PANTRY.x, top: PANTRY.y, width: PANTRY.w, height: PANTRY.h }} />
            <div className="viz-label absolute" style={{ left: PANTRY.x + 6, top: PANTRY.y + 4 }}>pantry</div>
            <div className="viz-ptable absolute" style={{ left: PANTRY_TABLE.x, top: PANTRY_TABLE.y, width: PANTRY_TABLE.w, height: PANTRY_TABLE.h }} />
            <div className="viz-coffee absolute" style={{ left: 40, top: 500 }} />
            <div className="viz-fridge absolute" style={{ left: 256, top: 495 }} />
            {/* server room */}
            <div className="viz-room viz-server absolute" style={{ left: SERVER_ROOM.x, top: SERVER_ROOM.y, width: SERVER_ROOM.w, height: SERVER_ROOM.h }} />
            <div className="viz-roomdoor absolute" style={{ left: SERVER_ROOM.doorX, top: SERVER_ROOM.y + SERVER_ROOM.h - 5, width: SERVER_ROOM.doorW, height: 8 }} />
            <div className="viz-rack absolute" style={{ left: 750, top: 70 }} />
            <div className="viz-rack absolute" style={{ left: 850, top: 70 }} />
            <div className="viz-label absolute" style={{ left: SERVER_ROOM.x + 8, top: SERVER_ROOM.y + 8 }}>server room</div>
            {SERVER_DESKS.map((d, i) => (
              <div key={`sd${i}`} className="absolute" style={{ left: d.x - 24, top: d.y + 18 }}>
                <div className="viz-desk viz-desk-sm" />
                <div className="viz-monitor" style={{ left: 14 }} />
              </div>
            ))}
            {/* meeting room */}
            <div className="viz-room viz-meeting absolute" style={{ left: MEETING_ROOM.x, top: MEETING_ROOM.y, width: MEETING_ROOM.w, height: MEETING_ROOM.h }} />
            <div className="viz-roomdoor absolute" style={{ left: MEETING_ROOM.doorX, top: MEETING_ROOM.y - 3, width: MEETING_ROOM.doorW, height: 8 }} />
            <div className="viz-mtable absolute" style={{ left: MEETING_TABLE.x, top: MEETING_TABLE.y, width: MEETING_TABLE.w, height: MEETING_TABLE.h }} />
            <div className="viz-label absolute" style={{ left: MEETING_ROOM.x + 8, top: MEETING_ROOM.y + 8 }}>meeting</div>
            {/* desks */}
            {DESKS.map((d, i) => (
              <div key={i} className="absolute" style={{ left: d.x - 34, top: d.y + 20 }}>
                <div className="viz-desk" />
                <div className="viz-monitor" />
                <div className="viz-lamp" />
              </div>
            ))}
            {/* night tint above props, below actors */}
            <div className="viz-tint absolute inset-0" />

            {/* characters */}
            {visible.map((e) => {
              const running = runningByPersona.get(e.name);
              const isWorking = e.mode === "working" && !!running;
              const failedAt = fails[e.name];
              const hasFail = failedAt !== undefined && now - failedAt < 5 * 60_000;
              const mail = unread[e.name] ?? 0;
              return (
                <div
                  key={e.name}
                  ref={(el) => { actorEls.current.set(e.name, el); }}
                  className="viz-actor absolute cursor-pointer"
                  style={{ left: 0, top: 0 }}
                  onClick={() => setSelected(e.name)}
                  title={`${e.name} — ${e.mode}`}
                >
                  <div className="viz-shadow" />
                  {isWorking && <div className="viz-typing" />}
                  {hasFail && <div className="viz-angry">💥</div>}
                  {star?.name === e.name && <div className="viz-star">⭐</div>}
                  {dones[e.name] !== undefined && now - dones[e.name] < 8000 && <div className="viz-done">✅</div>}
                  <div className={isWorking ? "viz-bob-fast" : "viz-bob"}>
                    <div ref={(el) => { walkEls.current.set(e.name, el); }}>
                    <div
                      ref={(el) => { flipEls.current.set(e.name, el); }}
                      style={{ width: TILE * ZOOM, height: TILE * ZOOM }}
                    >
                      {spriteUrls.has(e.name) ? (
                        <img
                          src={spriteUrls.get(e.name)}
                          alt={e.name}
                          className="viz-sprite"
                          style={{ width: TILE, height: TILE, transform: `scale(${ZOOM})`, transformOrigin: "top left" }}
                          draggable={false}
                        />
                      ) : (
                        <div
                          className="viz-sprite"
                          style={{ width: TILE, height: TILE, backgroundImage: `url(${SHEET})`, transform: `scale(${ZOOM})`, transformOrigin: "top left", ...spriteFor(e.name) }}
                        />
                      )}
                    </div>
                    </div>
                  </div>
                  <div className={hasFail ? "viz-nameplate viz-nameplate-alert" : "viz-nameplate"}>
                    {e.name}
                    {mail > 0 && <span className="viz-mail"> 📬{mail}</span>}
                  </div>
                  {isWorking && running && <Bubble spawnId={running.id} visitorPresent={speakingVisitors.has(running.id)} />}
                  {!isWorking && e.action && <div className="viz-minibubble">{e.action}</div>}
                </div>
              );
            })}

            {/* visitors — the contacts whose messages the fleet is answering */}
            {(snap?.visitors ?? []).map((v) => (
              <div
                key={`v:${v.id}`}
                ref={(el) => { actorEls.current.set(`v:${v.id}`, el); }}
                className="viz-actor absolute"
                style={{ left: 0, top: 0 }}
                title={`${v.name} · ${v.platform}${v.group ? " group" : ""} → ${v.persona}`}
              >
                <div className="viz-shadow" />
                <div className="viz-bob">
                  <div ref={(el) => { walkEls.current.set(`v:${v.id}`, el); }}>
                    <div ref={(el) => { flipEls.current.set(`v:${v.id}`, el); }} style={{ width: TILE * ZOOM, height: TILE * ZOOM }}>
                      <VisitorSprite name={v.name} contactId={v.contact_id} />
                    </div>
                  </div>
                </div>
                <div className={`viz-nameplate viz-np-${v.platform}`}>{v.group ? "👥 " : ""}{v.name}</div>
                {v.phase === "standing" && v.snippet && <div className="viz-minibubble">{v.snippet}</div>}
              </div>
            ))}

            {/* observed-chat clusters — the outside world chatting in the garden */}
            {OfficeSim.clusterLayout(snap?.observed ?? []).map(({ spec, anchor }) => (
              <button
                key={spec.chat_id}
                type="button"
                className={`viz-cluster viz-np-${spec.platform} absolute`}
                style={{ left: anchor.x - 75, top: anchor.y + 36, width: 150 }}
                onClick={() => setSelectedChat(spec)}
                title={`observed by ${spec.persona} — click for details`}
              >
                👁 {spec.title ?? spec.chat_id}
              </button>
            ))}
            {(snap?.guests ?? []).map((g) => (
              <div
                key={`g:${g.key}`}
                ref={(el) => { actorEls.current.set(`g:${g.key}`, el); }}
                className="viz-actor absolute cursor-pointer"
                style={{ left: 0, top: 0 }}
                onClick={() => { const o = (snap?.observed ?? []).find((s) => s.chat_id === g.chat_id); if (o) setSelectedChat(o); }}
                title={`${g.name} — in an observed chat`}
              >
                <div className="viz-shadow" />
                <div className="viz-bob">
                  <div ref={(el) => { walkEls.current.set(`g:${g.key}`, el); }}>
                    <div ref={(el) => { flipEls.current.set(`g:${g.key}`, el); }} style={{ width: TILE * ZOOM, height: TILE * ZOOM }}>
                      <VisitorSprite name={g.name} contactId={g.contact_id} />
                    </div>
                  </div>
                </div>
                <div className={`viz-nameplate viz-np-${g.platform}`}>{g.name}</div>
                {g.action && <div className="viz-minibubble">{g.action}</div>}
              </div>
            ))}

            {/* the office cat */}
            {snap?.cat && (
              <div ref={(el) => { catEl.current = el; }} className="viz-actor absolute" style={{ left: 0, top: 0 }} title="office cat">
                <div className="viz-shadow viz-shadow-sm" />
                <div ref={(el) => { catFlipEl.current = el; }} style={{ width: 32, height: 32 }}>
                  <img src={SpritesApi.pngUrl("_cat")} alt="office cat" className="viz-sprite" style={{ width: 32, height: 32 }} draggable={false} />
                </div>
                {snap.cat.action && <div className="viz-minibubble">{snap.cat.action}</div>}
              </div>
            )}

            {visible.length === 0 && (
              <div className="absolute inset-0 grid place-items-center viz-label">nobody's in — the office is empty</div>
            )}
          </div>
        </div>

        {selected && (
          <DialogBox
            persona={selected}
            entity={entities.find((e) => e.name === selected)}
            running={runningByPersona.get(selected)}
            spriteUrl={spriteUrls.get(selected)}
            onClose={() => setSelected(null)}
          />
        )}
        {showApprovals && (
          <div className="viz-dialog">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">Operator desk</span>
                <span className="viz-badge">{waitingOnHuman.length} waiting on you</span>
                <button className="ml-auto text-fg-subtle hover:text-fg" onClick={() => setShowApprovals(false)}><X className="size-4" /></button>
              </div>
              <div className="viz-dialog-text">
                {approvals.length === 0 && <p className="opacity-60">no pending assist sessions — desk is clear.</p>}
                {approvals.map((a) => (
                  <p key={a.sid}>
                    <span className="font-mono">[{a.token}]</span>{" "}
                    <span className={a.state === "awaiting_human" ? "text-warning" : "opacity-70"}>{a.state.replace(/_/g, " ")}</span>{" "}
                    · {a.observer} → {a.broker} · {a.title ?? a.subject_chat} · {agoText(Date.now() - Date.parse(a.created_at))} ago
                    {a.state === "awaiting_human" && <span className="opacity-60"> — reply "{a.token} yes/no" in the report chat</span>}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}
        {selectedChat && (
          <div className="viz-dialog">
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2 mb-1">
                <div className="min-w-0 flex-1 flex items-center gap-x-2 gap-y-1 flex-wrap">
                  <span className="font-semibold [overflow-wrap:anywhere]">{selectedChat.title ?? selectedChat.chat_id}</span>
                  <span className="viz-badge">{selectedChat.platform}</span>
                  <span className="text-[10px] opacity-60 whitespace-nowrap">observed by {selectedChat.persona}</span>
                </div>
                <button className="flex-shrink-0 text-fg-subtle hover:text-fg" onClick={() => setSelectedChat(null)}><X className="size-4" /></button>
              </div>
              <div className="viz-dialog-text">
                <p><span className="opacity-60">people here:</span> {selectedChat.participants.map((p) => p.name).join(", ")}</p>
                <p className="opacity-60">{selectedChat.persona} quietly watches this chat — it ingests what's said into memory and only steps in when there's a clear, high-value way to help.</p>
              </div>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}

/** Visitor sprite: the contact's generated character if it exists, otherwise
 *  a hash-picked tile — same fallback the personas use. */
function VisitorSprite({ name, contactId }: { name: string; contactId: number | null }) {
  const [broken, setBroken] = useState(false);
  if (contactId !== null && !broken) {
    return (
      <img
        src={SpritesApi.contactPngUrl(contactId)}
        alt={name}
        className="viz-sprite"
        style={{ width: TILE, height: TILE, transform: `scale(${ZOOM})`, transformOrigin: "top left" }}
        draggable={false}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div
      className="viz-sprite"
      style={{ width: TILE, height: TILE, backgroundImage: `url(${SHEET})`, transform: `scale(${ZOOM})`, transformOrigin: "top left", ...spriteFor(name) }}
    />
  );
}

/** Floating speech bubble above a working character. `visitorPresent` means the
 *  sender's own sprite is on screen with the inbound message — see bubbleText. */
function Bubble({ spawnId, visitorPresent }: { spawnId: string; visitorPresent?: boolean }) {
  const { data } = useQuery({
    queryKey: qk.spawns.detail(spawnId),
    queryFn: () => SpawnsApi.detail(spawnId),
    refetchInterval: 2500,
  });
  const text = data ? bubbleText(data, visitorPresent) : "";
  return <div className="viz-bubble">{text || "…"}</div>;
}

/** Classic bottom RPG dialog box for the selected character. */
function DialogBox({ persona, entity, running, spriteUrl, onClose }: {
  persona: string;
  entity?: SimEntity;
  running?: SpawnMeta;
  spriteUrl?: string;
  onClose: () => void;
}) {
  // Show the persona's running spawn if any, else its most recent spawn.
  const { data: list = [] } = useQuery({ queryKey: qk.spawns.list({ viz: 1, p: persona }), queryFn: () => SpawnsApi.list({ persona, limit: 5 }), refetchInterval: 3000 });
  const spawnId = running?.id ?? list[0]?.id;
  const { data } = useQuery({
    queryKey: qk.spawns.detail(spawnId ?? "none"),
    queryFn: () => SpawnsApi.detail(spawnId!),
    enabled: !!spawnId,
    refetchInterval: running ? 2500 : false,
  });
  const lines = data ? spokenLines(data.stdout).slice(-6) : [];
  const lastAt = list[0]?.started_at ? Date.parse(list[0].started_at) : null;
  const ago = lastAt ? agoText(Date.now() - lastAt) : null;
  const failedTail = data && data.meta.status === "failed"
    ? data.stderr.split("\n").filter((l) => l.trim()).slice(-3)
    : [];
  return (
    <div className="viz-dialog">
      {spriteUrl ? (
        <img src={spriteUrl} alt={persona} className="viz-dialog-portrait-img" draggable={false} />
      ) : (
        <div className="viz-dialog-portrait" style={{ backgroundImage: `url(${SHEET})`, ...spriteFor(persona) }} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2 mb-1">
          <div className="min-w-0 flex-1 flex items-center gap-x-2 gap-y-1 flex-wrap">
            <span className="font-semibold">{persona}</span>
            <span className="viz-badge">{entity?.mode ?? (running ? "working" : "idle")}</span>
            {ago && <span className="text-[10px] opacity-60 whitespace-nowrap">last spawn {ago} ago</span>}
            <a href={`#/spawns?persona=${encodeURIComponent(persona)}`} className="viz-link">📜 spawns</a>
            <a href={`#/personas?p=${encodeURIComponent(persona)}`} className="viz-link">⚙ persona</a>
          </div>
          <button className="flex-shrink-0 text-fg-subtle hover:text-fg" onClick={onClose}><X className="size-4" /></button>
        </div>
        <div className="viz-dialog-text">
          {failedTail.length > 0 && (
            <div className="viz-dialog-err">
              <p className="font-semibold">last spawn failed:</p>
              {failedTail.map((l, i) => <p key={`e${i}`}>{l}</p>)}
            </div>
          )}
          {lines.length ? lines.map((l, i) => <p key={i}>{l}</p>) : <p className="opacity-60">{running ? "…thinking…" : "nothing to say right now."}</p>}
        </div>
      </div>
    </div>
  );
}

function agoText(ms: number): string {
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "moments";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d`;
}

const keyframes = `
.viz-viewport { background:#1c1826; }
:root[data-theme="light"] .viz-viewport { background:#d9cdb8; }
.viz-stage { background:#2b2436; image-rendering: pixelated; }
/* wood planks: horizontal boards + subtle vertical seams */
.viz-floor { background:
  repeating-linear-gradient(90deg, rgba(0,0,0,.10) 0 2px, transparent 2px 112px),
  repeating-linear-gradient(0deg, #3b3252 0 26px, rgba(0,0,0,.14) 26px 28px, #362f4c 28px 54px, rgba(0,0,0,.14) 54px 56px); }
.viz-wall { background: linear-gradient(#241f30,#1c1826); border-bottom:4px solid #14101c; box-shadow:0 4px 0 rgba(0,0,0,.25); }
:root[data-theme="light"] .viz-stage { background:#efe7db; }
:root[data-theme="light"] .viz-floor { background:
  repeating-linear-gradient(90deg, rgba(120,90,50,.12) 0 2px, transparent 2px 112px),
  repeating-linear-gradient(0deg, #e3d4b6 0 26px, rgba(120,90,50,.18) 26px 28px, #dccaa8 28px 54px, rgba(120,90,50,.18) 54px 56px); }
:root[data-theme="light"] .viz-wall { background: linear-gradient(#cbb89a,#a8926f); border-bottom:4px solid #8f7a5a; box-shadow:0 4px 0 rgba(90,70,45,.2); }
/* garden — real WITA time sets the light */
.viz-garden { background:
  radial-gradient(circle 2px at 30px 40px, rgba(255,255,255,.10) 1px, transparent 2px),
  repeating-linear-gradient(0deg, #3f7a44 0 32px, #3a7240 32px 64px); border-left:0; }
.viz-phase-night .viz-garden { background:
  repeating-linear-gradient(0deg, #22422c 0 32px, #1f3d28 32px 64px); }
.viz-phase-dusk .viz-garden, .viz-phase-dawn .viz-garden { background:
  repeating-linear-gradient(0deg, #557041 0 32px, #4f683c 32px 64px); }
.viz-fence { background:#7a5a38; border:1px solid #4a3520; }
.viz-pond { border-radius:50%; background: radial-gradient(circle at 40% 35%, #7fd4e8, #3f8fb5 70%, #2f6f92); border:3px solid #2a5a75; }
.viz-phase-night .viz-pond { background: radial-gradient(circle at 40% 35%, #35597a, #24425e 70%, #1c3349); border-color:#16283a; }
.viz-tree { width:32px; height:40px; }
.viz-tree:before { content:""; position:absolute; left:0; top:0; width:32px; height:28px; background:#2f7d4f; border-radius:10px;
  box-shadow: -5px 8px 0 -3px #276a42, 5px 8px 0 -3px #3c9a62; }
.viz-tree:after { content:""; position:absolute; left:12px; bottom:0; width:8px; height:14px; background:#6b4526; }
.viz-phase-night .viz-tree:before { background:#22593a; box-shadow: -5px 8px 0 -3px #1d4d32, 5px 8px 0 -3px #2a6b45; }
.viz-bench { width:48px; height:14px; background:linear-gradient(#a9743f,#8a5a34); border:2px solid #4a2f19; border-radius:2px; box-shadow:0 3px 0 #3a2414; }
.viz-flowers { width:34px; height:12px; background:
  radial-gradient(circle 3px at 4px 6px, #e86a8a 2px, transparent 3px),
  radial-gradient(circle 3px at 14px 4px, #f2d14f 2px, transparent 3px),
  radial-gradient(circle 3px at 24px 7px, #b57ae0 2px, transparent 3px),
  radial-gradient(circle 3px at 31px 4px, #e86a6a 2px, transparent 3px); }
/* windows: sky follows the WITA clock */
.viz-window { width:64px; height:38px; border:3px solid #14101c; border-radius:3px; background: linear-gradient(#9fd2f2,#cfeafa); }
.viz-phase-day .viz-window { background: radial-gradient(circle 6px at 50px 10px, #ffd75e 5px, rgba(255,215,94,.35) 8px, transparent 9px), linear-gradient(#9fd2f2,#cfeafa); }
.viz-phase-dusk .viz-window { background: radial-gradient(circle 6px at 50px 26px, #ff9d4d 5px, rgba(255,157,77,.4) 9px, transparent 10px), linear-gradient(#5a4a7a,#e8845a); }
.viz-phase-dawn .viz-window { background: linear-gradient(#7a6a9a,#f2b48a); }
.viz-phase-night .viz-window { background:
  radial-gradient(circle 5px at 50px 10px, #f2edd5 4px, transparent 5px),
  radial-gradient(circle 1.5px at 12px 12px, #fff 1px, transparent 2px),
  radial-gradient(circle 1.5px at 26px 22px, #ffe9b0 1px, transparent 2px),
  radial-gradient(circle 1.5px at 38px 8px, #fff 1px, transparent 2px),
  linear-gradient(#101a38,#2a3a63); }
.viz-window:after { content:""; position:absolute; left:50%; top:0; bottom:0; width:3px; margin-left:-1.5px; background:#14101c; }
:root[data-theme="light"] .viz-window { border-color:#8f7a5a; }
:root[data-theme="light"] .viz-window:after { background:#8f7a5a; }
/* night tint over the scene (below the actors) + desk lamps */
.viz-tint { pointer-events:none; z-index:60; background:#0a0a28; opacity:0; transition:opacity 2s; }
.viz-phase-night .viz-tint { opacity:.30; }
.viz-phase-dusk .viz-tint { background:#3a1a08; opacity:.14; }
.viz-phase-dawn .viz-tint { background:#2a1a30; opacity:.12; }
.viz-lamp { position:absolute; left:-6px; top:-14px; width:80px; height:54px; border-radius:50%; pointer-events:none;
  background: radial-gradient(ellipse at center, rgba(255,214,130,.5), transparent 65%); opacity:0; transition:opacity 2s; z-index:61; }
.viz-phase-night .viz-lamp { opacity:.9; }
.viz-phase-dusk .viz-lamp { opacity:.5; }
/* office door on the back wall */
.viz-door { width:30px; height:46px; background:linear-gradient(#7a4e2d,#5d3a20); border:3px solid #3a2414; border-radius:3px 3px 0 0; }
.viz-door:after { content:""; position:absolute; right:4px; top:22px; width:4px; height:4px; border-radius:50%; background:#e8c56a; }
:root[data-theme="light"] .viz-door { border-color:#6b4a2a; }
/* whiteboard with scribbles */
.viz-board { width:92px; height:40px; background:#f4f2ea; border:3px solid #8a8577; border-radius:2px; }
.viz-board:before { content:""; position:absolute; left:8px; right:30px; top:7px; height:20px;
  background: repeating-linear-gradient(0deg, rgba(70,110,200,.55) 0 2px, transparent 2px 7px); }
.viz-board:after { content:""; position:absolute; right:8px; top:9px; width:16px; height:12px;
  border:2px solid rgba(200,80,80,.6); border-radius:2px; }
/* wall clock */
.viz-clock { width:18px; height:18px; border-radius:50%; background:#f2f1ec; border:3px solid #14101c; }
.viz-clock:before { content:""; position:absolute; left:50%; top:50%; width:2px; height:6px; margin:-6px 0 0 -1px; background:#333; }
.viz-clock:after { content:""; position:absolute; left:50%; top:50%; width:5px; height:2px; margin:-1px 0 0 -1px; background:#333; }
:root[data-theme="light"] .viz-clock { border-color:#8f7a5a; }
/* patterned rug */
.viz-rug { border-radius:6px; border:3px solid #5d4070; box-shadow: inset 0 0 0 4px rgba(0,0,0,.18);
  background: repeating-linear-gradient(45deg, rgba(255,255,255,.05) 0 8px, transparent 8px 16px), linear-gradient(#4a3157,#402b4c); }
:root[data-theme="light"] .viz-rug { border-color:#b08d5f; box-shadow: inset 0 0 0 4px rgba(120,90,50,.15);
  background: repeating-linear-gradient(45deg, rgba(120,90,50,.10) 0 8px, transparent 8px 16px), linear-gradient(#dcc69c,#d2ba8d); }
/* glass rooms */
.viz-room { border:4px solid rgba(140,190,230,.55); border-radius:4px; background:rgba(120,170,220,.07); box-shadow: inset 0 0 0 1px rgba(255,255,255,.12); }
.viz-server { background:rgba(90,120,200,.10); }
:root[data-theme="light"] .viz-room { border-color:rgba(90,120,150,.5); }
.viz-roomdoor { background:transparent; border-left:3px solid rgba(140,190,230,.55); border-right:3px solid rgba(140,190,230,.55); z-index:2; }
.viz-rack { width:30px; height:34px; background:linear-gradient(#3c4454,#262c38); border:2px solid #171b22; border-radius:2px; }
.viz-rack:before { content:""; position:absolute; left:4px; top:5px; width:5px; height:5px; border-radius:50%; background:#5aff8a; box-shadow:0 8px 0 #ffb54d, 0 16px 0 #5ad1ff; animation: vizBlink 1.6s infinite; }
.viz-rack:after { content:""; position:absolute; right:5px; top:5px; width:12px; height:2px; background:#5a6478; box-shadow:0 5px 0 #5a6478, 0 10px 0 #5a6478, 0 15px 0 #5a6478, 0 20px 0 #5a6478; }
@keyframes vizBlink { 0%,100%{ opacity:1 } 50%{ opacity:.35 } }
/* meeting + pantry furniture */
.viz-mtable { background:linear-gradient(#8a5a34,#6b4526); border:2px solid #4a2f19; border-radius:8px; box-shadow:0 3px 0 #3a2414; }
.viz-pantry { border-radius:6px; background:rgba(255,255,255,.035); border:2px dashed rgba(255,255,255,.12); }
:root[data-theme="light"] .viz-pantry { background:rgba(120,90,50,.06); border-color:rgba(120,90,50,.25); }
.viz-ptable { background:linear-gradient(#8a5a34,#6b4526); border:2px solid #4a2f19; border-radius:4px; box-shadow:0 3px 0 #3a2414; }
.viz-fridge { width:32px; height:36px; background:linear-gradient(#c8ccd4,#9aa0ac); border:2px solid #5a606c; border-radius:3px; }
.viz-fridge:before { content:""; position:absolute; left:3px; top:14px; right:3px; height:2px; background:#5a606c; }
/* coffee corner: counter + machine + mug */
.viz-coffee { width:46px; height:22px; background:linear-gradient(#7a4e2d,#5d3a20); border:2px solid #3a2414; border-radius:3px; box-shadow:0 3px 0 #3a2414; }
.viz-coffee:before { content:""; position:absolute; left:5px; top:-15px; width:16px; height:15px; background:linear-gradient(#3c4454,#262c38); border:2px solid #171b22; border-radius:2px; }
.viz-coffee:after { content:""; position:absolute; right:7px; top:-6px; width:8px; height:6px; background:#d9534f; border:1px solid #7a2f2c; border-radius:1px; }
/* potted plant */
.viz-plant { width:22px; height:36px; }
.viz-plant:before { content:""; position:absolute; left:0; top:0; width:22px; height:22px; background:#2f7d4f; border-radius:8px 8px 5px 5px;
  box-shadow: -4px 6px 0 -2px #276a42, 4px 6px 0 -2px #3c9a62, 0 -3px 0 -1px #3c9a62; }
.viz-plant:after { content:""; position:absolute; left:3px; bottom:0; width:16px; height:13px; background:linear-gradient(#8a5a34,#6b4526); border:2px solid #4a2f19; border-radius:0 0 3px 3px; }
/* HUD, ticker, zoom controls */
.viz-hud { font-size:11px; line-height:1; padding:6px 8px; border-radius:6px; background:rgba(20,16,28,.72); color:#eae6f2; border:1px solid rgba(255,255,255,.18); }
.viz-hud-alert { color:#ff7a6a; }
:root[data-theme="light"] .viz-hud { background:rgba(255,252,244,.85); color:#241f30; border-color:rgba(90,70,45,.35); }
.viz-ticker { font-size:11px; line-height:1.2; padding:5px 9px; border-radius:6px; background:rgba(20,16,28,.72); color:#eae6f2; border:1px solid rgba(255,255,255,.18); max-width:70%; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; animation: vizFadeIn .4s; }
:root[data-theme="light"] .viz-ticker { background:rgba(255,252,244,.9); color:#241f30; border-color:rgba(90,70,45,.35); }
@keyframes vizFadeIn { from { opacity:0; transform:translateY(4px) } to { opacity:1; transform:none } }
.viz-zoombtn { display:grid; place-items:center; width:30px; height:30px; border-radius:6px; background:rgba(20,16,28,.72); color:#eae6f2; border:1px solid rgba(255,255,255,.18); }
.viz-zoombtn:hover { background:rgba(40,34,56,.85); }
:root[data-theme="light"] .viz-zoombtn { background:rgba(255,252,244,.85); color:#241f30; border-color:rgba(90,70,45,.35); }
:root[data-theme="light"] .viz-zoombtn:hover { background:#fff; }
.viz-sprite { image-rendering: pixelated; background-repeat: no-repeat; }
.viz-actor { z-index: 100; will-change: transform; }
.viz-nameplate { position:absolute; top:100%; left:50%; transform:translateX(-50%); margin-top:2px; font-size:10px; white-space:nowrap; color:#fff; text-shadow:0 1px 2px #000; background:rgba(0,0,0,.35); padding:0 4px; border-radius:3px; }
.viz-nameplate-alert { background:rgba(180,30,20,.75); animation: vizAlert 1s infinite; }
/* visitor nameplates, colored by source platform */
.viz-np-whatsapp { background:rgba(30,140,80,.85); }
.viz-np-telegram { background:rgba(32,140,200,.85); }
.viz-np-slack { background:rgba(97,31,105,.85); }
.viz-np-gchat { background:rgba(60,100,200,.85); }
.viz-np-email { background:rgba(90,90,100,.85); }
/* observed-chat cluster label in the garden */
.viz-cluster { font-size:10px; line-height:1.2; color:#fff; text-shadow:0 1px 2px #000; padding:2px 6px; border-radius:4px; border:1px solid rgba(255,255,255,.25); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; z-index:95; }
/* operator desk + pending-approval papers */
.viz-opdesk { width:48px; height:22px; background:linear-gradient(#8a5a34,#6b4526); border:2px solid #4a2f19; border-radius:3px; box-shadow:0 3px 0 #3a2414; cursor:pointer; z-index:4; }
.viz-opdesk-label { position:absolute; top:100%; left:50%; transform:translateX(-50%); margin-top:2px; font-size:9px; color:rgba(255,255,255,.55); text-transform:uppercase; letter-spacing:.05em; }
:root[data-theme="light"] .viz-opdesk-label { color:rgba(60,40,20,.6); }
.viz-paper { position:absolute; width:10px; height:13px; background:#f4f2ea; border:1px solid #8a8577; border-radius:1px; }
.viz-paper:after { content:""; position:absolute; left:2px; top:3px; right:2px; height:1px; background:#9aa; box-shadow:0 3px 0 #9aa, 0 6px 0 #9aa; }
.viz-paper-hot { background:#fff3d6; border-color:#c9a94d; animation: vizAlert 1.4s infinite; }
.viz-link { font-size:10px; color:#5ad1ff; text-decoration:none; border:1px solid rgba(90,209,255,.35); border-radius:4px; padding:1px 6px; white-space:nowrap; }
.viz-link:hover { background:rgba(90,209,255,.15); }
@keyframes vizAlert { 0%,100%{ opacity:1 } 50%{ opacity:.55 } }
.viz-mail { color:#ffd76a; }
.viz-angry { position:absolute; left:60%; top:-16px; font-size:13px; animation: vizAlert 1s infinite; z-index:21; }
.viz-desk { width:68px; height:26px; background:linear-gradient(#8a5a34,#6b4526); border:2px solid #4a2f19; border-radius:3px; box-shadow:0 3px 0 #3a2414; }
.viz-desk-sm { width:48px; height:22px; }
.viz-monitor { position:absolute; left:24px; top:-10px; width:20px; height:14px; background:#1a2a3a; border:2px solid #0d151f; border-radius:2px; }
.viz-typing { position:absolute; left:26px; top:-30px; width:16px; height:10px; border-radius:2px; background:#5ad1ff; opacity:.0; animation: vizType 1.1s infinite; box-shadow:0 0 8px #5ad1ff; }
.viz-couch { background:linear-gradient(#b0483f,#8f3730); border:2px solid #5f231d; border-radius:5px; box-shadow:0 3px 0 #4a1a15; }
.viz-label { font-size:11px; letter-spacing:.05em; color:rgba(255,255,255,.45); text-transform:uppercase; z-index:3; }
:root[data-theme="light"] .viz-label { color: rgba(60,40,20,.5); }
.viz-bubble { position:absolute; bottom:100%; left:50%; transform:translateX(-50%); margin-bottom:6px; max-width:180px; width:max-content; background:#fff; color:#111; font-size:11px; line-height:1.25; padding:5px 8px; border-radius:8px; border:2px solid #111; box-shadow:0 3px 0 rgba(0,0,0,.3); z-index:20; }
.viz-bubble:after { content:""; position:absolute; top:100%; left:50%; transform:translateX(-50%); border:6px solid transparent; border-top-color:#111; }
.viz-minibubble { position:absolute; bottom:100%; left:50%; transform:translateX(-50%); margin-bottom:5px; width:max-content; max-width:150px; background:rgba(255,255,255,.92); color:#222; font-size:10px; line-height:1.2; padding:2px 6px; border-radius:7px; border:1px solid rgba(0,0,0,.5); z-index:18; }
.viz-bob { animation: vizBob 2.4s ease-in-out infinite; }
.viz-bob-fast { animation: vizBob 1.2s ease-in-out infinite; }
/* walk wiggle + soft shadows + event pops */
.viz-walk { animation: vizWalk .45s ease-in-out infinite; transform-origin: 50% 90%; }
@keyframes vizWalk { 0%,100%{ transform:rotate(-3.5deg) } 50%{ transform:rotate(3.5deg) } }
.viz-shadow { position:absolute; left:50%; top:44px; width:30px; height:8px; transform:translateX(-50%); border-radius:50%; background:rgba(0,0,0,.28); }
.viz-shadow-sm { top:29px; width:22px; height:6px; }
.viz-star { position:absolute; left:50%; top:-26px; transform:translateX(-50%); font-size:13px; animation: vizBob 2s ease-in-out infinite; z-index:22; text-shadow:0 0 6px rgba(255,215,90,.9); }
.viz-done { position:absolute; left:70%; top:-18px; font-size:13px; animation: vizPop 1.2s ease-out; z-index:22; }
@keyframes vizPop { 0%{ transform:translateY(8px) scale(.4); opacity:0 } 30%{ transform:none; opacity:1 } 100%{ opacity:1 } }
/* rain over the garden */
.viz-rain { pointer-events:none; z-index:65; opacity:.55; background:
  repeating-linear-gradient(75deg, transparent 0 7px, rgba(180,210,255,.45) 7px 9px);
  animation: vizRain .5s linear infinite; }
@keyframes vizRain { from { background-position:0 0 } to { background-position:-18px 66px } }
/* star-of-the-day wall plaque */
.viz-plaque { display:flex; flex-direction:column; align-items:center; gap:1px; padding:3px 7px; background:linear-gradient(#3a3148,#2c2538); border:2px solid #c9a94d; border-radius:3px; z-index:3; }
.viz-plaque-title { font-size:7px; letter-spacing:.08em; text-transform:uppercase; color:#c9a94d; line-height:1; }
.viz-plaque-name { font-size:10px; color:#f2e9c9; line-height:1.1; white-space:nowrap; }
:root[data-theme="light"] .viz-plaque { background:linear-gradient(#8a744a,#6e5a38); }
@keyframes vizBob { 0%,100%{ transform:translateY(0)} 50%{ transform:translateY(-3px)} }
@keyframes vizType { 0%,100%{ opacity:0 } 50%{ opacity:.9 } }
.viz-dialog { margin:16px auto 0; max-width:1180px; display:flex; gap:12px; background:#12101a; border:3px solid #5ad1ff; border-radius:10px; padding:12px 14px; box-shadow:0 0 0 3px #12101a, 0 4px 20px rgba(0,0,0,.4); color:#eae6f2; }
:root[data-theme="light"] .viz-dialog { background:#fdfbf6; color:#241f30; border-color:#3a6ea5; box-shadow:0 4px 16px rgba(0,0,0,.15); }
.viz-dialog-portrait { width:48px; height:48px; flex:none; image-rendering:pixelated; background-repeat:no-repeat; transform:scale(3); transform-origin:top left; margin:16px 28px 0 4px; }
.viz-dialog-portrait-img { width:48px; height:48px; flex:none; image-rendering:pixelated; margin:4px 8px 0 4px; }
.viz-dialog-text { font-size:13px; line-height:1.4; max-height:120px; overflow:auto; }
.viz-dialog-text p { margin:0 0 3px; }
.viz-dialog-err { color:#ff8a7a; border-left:3px solid #ff8a7a; padding-left:8px; margin-bottom:6px; font-size:12px; }
.viz-badge { font-size:10px; text-transform:uppercase; letter-spacing:.05em; padding:1px 6px; border-radius:999px; background:rgba(90,209,255,.18); color:#5ad1ff; }
`;
